 
import React, { memo, useState } from 'react';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { useDispatch } from 'react-redux';

import ChevronRight from '../../assets/images/chevron-right.svg';
import {
	useCreateFlexShiftsMutation,
	useCreateHybridShiftMutation,
	useCreateHybridSlotsMutation,
	useCreateOnDemandShiftMutation,
	useCreateOnDemandSlotsMutation
} from '../../redux/globalOverview';
import useMediaQuery, { MOBILE_WIDTH_QUERY } from '../../components/hooks/useMediaQuery';
import { GLOBAL_CALENDAR_SHIFT_TYPES } from '../../constants';
import AddSlots from '../shifts/daily-schedule/AddSlots';
import facilityInfoIcon from '../../assets/images/facility-info-icon.svg';
import { showNotification } from '../../redux/notification';
import ConfirmationModal from '../../components/modals/ConfirmationModalNew';
import { isOpenSlot } from './shiftActionSlots';

const getEndUserMessage = (res) => {
	const validationError = res && res.data && res.data.validationError;
	if (validationError) {
		if (res && res.data && res.data.validationError) {
			const errorMessage = res.data.errors[0].message;
			if (errorMessage) {
				return errorMessage;
			}
		}
	}
	return null;
};

const getRequestSlotTitle = (shiftRequestType, isMobile) => {
	if (shiftRequestType === 'on-demand') {
		return 'Add new On Demand shifts';
	}

	if (shiftRequestType === 'on-call') {
		return 'Add new On Call shifts';
	}

	if (shiftRequestType === 'flex') {
		return 'Add new Internal Flex shifts';
	}

	if (shiftRequestType === 'hybrid') {
		return (
			<div className="request-shift-title">
				<span>Add new Hybrid shifts</span>
				<OverlayTrigger
					className="tooltip-component"
					placement={isMobile ? 'bottom' : 'right'}
					delay={{ show: 100, hide: 400 }}
					overlay={
						<Tooltip
							delay={{ show: 150, hide: 400 }}
							className={classNames('info-facility-tooltip', { 'shift-bottom-placement': isMobile })}
							id="add-hybrid-slots-tooltip"
						>
							Hybrid shifts are open to both Flex and ShiftMed professionals and are filled on a first come first serve
							basis. If you want to limit which group can claim, you can use the request On Demand or request Flex shift
							options.
						</Tooltip>
					}
				>
					<img className="tooltip-title" src={facilityInfoIcon} alt="info" />
				</OverlayTrigger>
			</div>
		);
	}

	return '';
};

const AddSlotsMenuItem = ({
	shift,
	facilityTimezone,
	showAddFlexOption,
	showAddOnDemandOption,
	showAddHybridOption,
	showAddOnCallOption
}) => {
	const dispatch = useDispatch();
	const facilityId = shift?.facility_id;
	const [shiftRequestType, setShiftRequestType] = useState(null);
	const [numberOfSlots, setNumberOfSlots] = useState(1);
	const isMobile = useMediaQuery(MOBILE_WIDTH_QUERY);

	const [createFlexShift, { isLoading: isCreateFlexShiftLoading }] = useCreateFlexShiftsMutation();
	const [createOnDemandSlots, { isLoading: isCreateOnDemandSlotsLoading }] = useCreateOnDemandSlotsMutation();
	const [createOnDemandShift, { isLoading: isCreateOnDemandShiftLoading }] = useCreateOnDemandShiftMutation();
	const [createHybridShift, { isLoading: isCreateHybridShiftLoading }] = useCreateHybridShiftMutation();
	const [createHybridSlots, { isLoading: isCreateHybridSlotsLoading }] = useCreateHybridSlotsMutation();
	const isLoading =
		isCreateHybridSlotsLoading ||
		isCreateOnDemandShiftLoading ||
		isCreateOnDemandSlotsLoading ||
		isCreateHybridShiftLoading ||
		isCreateFlexShiftLoading;

	const onDemandRouteData = shift.routeData[GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND];
	const unfilledOnDemandSlots = onDemandRouteData ? onDemandRouteData.rawShift.slots.filter(isOpenSlot) : [];
	const shiftIsGuaranteed =
		unfilledOnDemandSlots.length > 0 && unfilledOnDemandSlots.every((slot) => slot.is_guaranteed);
	const startTime = moment.tz(shift.start_time_utc, facilityTimezone);

	const addFlexSlots = async () => {
		await createFlexShift({
			facilityId,
			structureId: shift.shift_structure_id,
			slotCount: numberOfSlots,
			startDate: startTime.format('YYYY-MM-DD')
		}).unwrap();
	};

	const addOnDemandSlots = async () => {
		const shiftDate = startTime.format('YYYY-MM-DD');

		// create a new shift if the group doesn't include an on demand shift
		const shouldCreateNewShift = !shift.shift_types.has(GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND);
		if (shouldCreateNewShift) {
			await createOnDemandShift({
				facilityId,
				shiftStructureId: shift.shift_structure_id,
				slotCount: numberOfSlots,
				shiftDate
			}).unwrap();
		} else {
			await createOnDemandSlots({
				shiftId: shift.routeData[GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND].shiftId,
				numberOfSlots,
				shiftDate,
				isRecurring: shift.routeData[GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND].isRecurring
			}).unwrap();
		}
	};

	const addHybridSlots = async () => {
		const shiftDate = startTime.format('YYYY-MM-DD');

		// create a new shift if the group doesn't include an on demand shift
		const shouldCreateNewShift = !shift.shift_types.has(GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND);
		if (shouldCreateNewShift) {
			await createHybridShift({
				facilityId,
				shiftStructureId: shift.shift_structure_id,
				slotCount: numberOfSlots,
				shiftDate
			}).unwrap();
		} else {
			await createHybridSlots({
				facilityId,
				shiftId: shift.routeData[GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND].shiftId,
				numberOfSlots,
				shiftDate,
				isRecurring: shift.routeData[GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND].isRecurring
			}).unwrap();
		}
	};

	const addSlots = async () => {
		if (!shiftRequestType) {
			return;
		}

		try {
			if (shiftRequestType === 'flex') {
				await addFlexSlots();
			}

			if (shiftRequestType === 'on-demand' || shiftRequestType === 'on-call') {
				await addOnDemandSlots();
			}

			if (shiftRequestType === 'hybrid') {
				await addHybridSlots();
			}

			setShiftRequestType(null);
			setNumberOfSlots(1);
			dispatch(showNotification('success', 'Shifts created successfully.'));
		} catch (e) {
			if (e.status !== 403) {
				let userMessage = getEndUserMessage(e);
				if (userMessage && userMessage.indexOf('is not empty') > 0) {
					userMessage = 'Data has changed. Please refresh the page.';
				}
				dispatch(showNotification('error', userMessage || 'Failed to add shifts. Please try again!'));
			}
		}
	};

	let menuComponents = [];

	if (showAddOnCallOption) {
		menuComponents.push(
			<Dropdown.Item
				key="add-on-call-slot-gs-action-menu-item"
				className="action-menu-item"
				onClick={() => setShiftRequestType('on-call')}
			>
				Add On Call shift
			</Dropdown.Item>
		);
	}

	const optionsToShow = [showAddOnDemandOption, showAddFlexOption, showAddHybridOption].filter(Boolean);

	if (!shift.is_on_call && optionsToShow.length === 1) {
		menuComponents = [
			showAddOnDemandOption && (
				<Dropdown.Item
					key="add-on-demand-slot-gs-action-menu-item"
					className="action-menu-item"
					onClick={() => setShiftRequestType('on-demand')}
				>
					Add On Demand shift
				</Dropdown.Item>
			),
			showAddFlexOption && (
				<Dropdown.Item
					key="add-flex-slot-gs-action-menu-item"
					className="action-menu-item"
					onClick={() => setShiftRequestType('flex')}
				>
					Add Internal Flex shift
				</Dropdown.Item>
			),
			showAddHybridOption && (
				<Dropdown.Item
					key="add-hybrid-slot-gs-action-menu-item"
					className="action-menu-item"
					onClick={() => setShiftRequestType('hybrid')}
				>
					Add Hybrid shift
				</Dropdown.Item>
			)
		];
	}

	if (!shift.is_on_call && optionsToShow.length > 1) {
		menuComponents = [
			<Dropdown key="add-slot-gs-action-menu" drop="end">
				<Dropdown.Toggle as="a" className="action-menu add-slots-menu-btn action-menu-item">
					<span className="label">Add shift</span>
					<img src={ChevronRight} className="action-menu-item-icon" alt="chevron-right" />
				</Dropdown.Toggle>
				<Dropdown.Menu>
					{showAddOnDemandOption && (
						<Dropdown.Item className="action-menu-item" onClick={() => setShiftRequestType('on-demand')}>
							On Demand
						</Dropdown.Item>
					)}
					{showAddFlexOption && (
						<Dropdown.Item className="action-menu-item" onClick={() => setShiftRequestType('flex')}>
							Internal Flex
						</Dropdown.Item>
					)}
					{showAddHybridOption && (
						<Dropdown.Item className="action-menu-item" onClick={() => setShiftRequestType('hybrid')}>
							Hybrid
						</Dropdown.Item>
					)}
				</Dropdown.Menu>
			</Dropdown>
		];
	}

	return menuComponents.length > 0
		? [
				...menuComponents,
				shiftRequestType !== null && (
					<ConfirmationModal
						key="gs-add-slot-modal"
						size="xl"
						title={getRequestSlotTitle(shiftRequestType, isMobile)}
						onConfirm={addSlots}
						onCancel={() => {
							setNumberOfSlots(1);
							setShiftRequestType(null);
						}}
						cancelLabel="Cancel"
						confirmLabel="Confirm"
						className="add-slot-modal"
						disableBackdrop
						showLoadingSpinner={isLoading}
						disableCancel={isLoading}
						showChildrenWhileLoading
					>
						<AddSlots
							numberOfSlots={numberOfSlots}
							setNumberOfSlots={setNumberOfSlots}
							shift={{
								...shift,
								slots: [
									{
										resource_type: `${shift.shift_resource_type}${
											shift.shift_specialty ? ` - ${shift.shift_specialty}` : ''
										}`
									}
								],
								start_time: shift.formatted_start_time,
								end_time: shift.formatted_end_time,
								isOnCall: shift.is_on_call,
								raw_start_time: `${startTime.format('YYYY-MM-DDTHH:mm:ss.SSS')}Z`,
								raw_end_time: `${moment.tz(shift.end_time_utc, facilityTimezone).format('YYYY-MM-DDTHH:mm:ss.SSS')}Z`
							}}
							showGuaranteed={shiftRequestType !== 'flex' && shiftIsGuaranteed}
							isMobile={isMobile}
						/>
					</ConfirmationModal>
				)
		  ]
		: null;
};

export default memo(AddSlotsMenuItem);
